<template>
  <div class="alphabet-filter-container">
    <div class="flex align-items-center gap-2 justify-content-center py-2">
      <!-- Reset filter icon -->
      <Button
        icon="pi pi-times"
        class="p-button-rounded"
        severity="secondary"
        raised
        @click="clearFilter"
      />

      <!-- Alphabet letters for filtering, scaled to fit the width -->
      <div class="alphabet-row">
        <Button
          v-for="letter in letters"
          :key="letter"
          :label="letter" 
          :class="['letter-item', selectedLetter === letter ? 'selected-letter' : 'unselected-letter']"
          rounded
          @click="applyLetterFilter(letter)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineEmits, defineProps } from 'vue';

const props = defineProps({
    apiFunction: {
        type: Function,
        required: false
    },
    data: {
        type: Array,
        required: false
    },
    filterKey: {
        type: String,
        required: false
    },
    loading: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['filtered', 'loading']);
const selectedLetter = ref(null);

// Alphabet letters to display
const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// Apply filter based on the clicked letter
const applyLetterFilter = async (letter) => {
    console.log('Letter clicked:', letter, 'Current selected:', selectedLetter.value);
    
    // Toggle the selected letter - if same letter is clicked, clear the filter
    if (selectedLetter.value === letter) {
        selectedLetter.value = null; // Clear the filter
        console.log('Clearing filter');
        await fetchData(); // Fetch all data
    } else {
        selectedLetter.value = letter; // Set the selected letter
        console.log('Setting filter to:', letter);
        await fetchData(letter); // Fetch filtered data
    }
};

// Clear the filter and show all data
const clearFilter = async () => {
    selectedLetter.value = null; // Reset the selected letter
    await fetchData(); // Fetch all data
};

// Generic function to fetch data from API or filter client-side data
const fetchData = async (letter = null) => {
    try {
        emit('loading', true);
        
        let response;
        
        if (props.apiFunction) {
            // API-based filtering
            response = await props.apiFunction(letter);
        } else if (props.data && props.filterKey) {
            // Client-side filtering
            if (letter) {
                response = props.data.filter(item => {
                    const value = item[props.filterKey];
                    return value && value.toString().toUpperCase().startsWith(letter);
                });
            } else {
                response = props.data;
            }
        } else {
            console.error('AlphabetFilter - No valid props provided for filtering');
            response = [];
        }
        
        // Emit the filtered data
        emit('filtered', response);
    } catch (error) {
        console.error('Error fetching data:', error);
        emit('filtered', []); // Emit empty array on error
    } finally {
        emit('loading', false);
    }
};
</script>

<style scoped>
.alphabet-filter-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

.alphabet-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
}

.letter-item {
    text-align: center;
    flex: 1;
    max-width: 40px;
    min-width: 40px;
    height: 40px;
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 0.2s ease;
}

/* Unselected letter styling */
.letter-item.unselected-letter {
    background-color: transparent !important;
    color: #64748b !important;
    border: 1px solid transparent !important;
}

.letter-item.unselected-letter:hover {
    background-color: #f1f5f9 !important;
    color: #475569 !important;
    border-color: #e2e8f0 !important;
}

/* Selected letter styling */
.letter-item.selected-letter {
    background-color: #3b82f6 !important;
    color: white !important;
    border: 1px solid #1d4ed8 !important;
    font-weight: 600 !important;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3) !important;
}

.letter-item.selected-letter:hover {
    background-color: #2563eb !important;
    color: white !important;
    border-color: #1d4ed8 !important;
}

.p-button-text {
    width: 100%;
}
</style>
